import { Alert, Box, Button, Paper, Stack, TextField, Typography } from "@mui/material";
import { confirmSignIn, signIn } from "aws-amplify/auth";
import { useState } from "react";

/**
 * Anmeldung samt erstmaliger Passwortvergabe.
 *
 * Cognito verlangt bei einem neu angelegten Nutzer ein neues Passwort
 * (`CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED`). Ohne diesen Schritt lässt
 * sich ein per Einladung erstellter Zugang gar nicht in Betrieb nehmen — es
 * gibt keine gehostete Anmeldeseite.
 */
export function Anmeldung({ onFertig }: { onFertig: () => void }) {
  const [benutzer, setBenutzer] = useState("");
  const [passwort, setPasswort] = useState("");
  const [neuesPasswort, setNeuesPasswort] = useState("");
  const [brauchtNeuesPasswort, setBrauchtNeuesPasswort] = useState(false);
  const [fehler, setFehler] = useState<string>();
  const [laeuft, setLaeuft] = useState(false);

  async function absenden(event: React.FormEvent) {
    event.preventDefault();
    setFehler(undefined);
    setLaeuft(true);
    try {
      if (brauchtNeuesPasswort) {
        const { isSignedIn } = await confirmSignIn({
          challengeResponse: neuesPasswort,
        });
        if (isSignedIn) onFertig();
        return;
      }

      const { isSignedIn, nextStep } = await signIn({
        username: benutzer,
        password: passwort,
      });
      if (isSignedIn) {
        onFertig();
      } else if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        setBrauchtNeuesPasswort(true);
      } else {
        setFehler(`Unerwarteter Anmeldeschritt: ${nextStep.signInStep}`);
      }
    } catch (error) {
      setFehler(error instanceof Error ? error.message : String(error));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        p: 2,
      }}
    >
      <Paper
        sx={{ p: 4, width: "100%", maxWidth: 380 }}
        component="form"
        onSubmit={absenden}
      >
        <Stack spacing={2}>
          <Typography variant="h5">HouseholdBook</Typography>

          {brauchtNeuesPasswort ? (
            <>
              <Alert severity="info">
                Beim ersten Anmelden muss ein eigenes Passwort vergeben werden.
              </Alert>
              <TextField
                label="Neues Passwort"
                type="password"
                value={neuesPasswort}
                onChange={(e) => setNeuesPasswort(e.target.value)}
                helperText="Mindestens 12 Zeichen, mit Klein- und Ziffernanteil"
                autoFocus
                required
              />
            </>
          ) : (
            <>
              <TextField
                label="Benutzername"
                value={benutzer}
                onChange={(e) => setBenutzer(e.target.value)}
                autoComplete="username"
                autoFocus
                required
              />
              <TextField
                label="Passwort"
                type="password"
                value={passwort}
                onChange={(e) => setPasswort(e.target.value)}
                autoComplete="current-password"
                required
              />
            </>
          )}

          {fehler && <Alert severity="error">{fehler}</Alert>}

          <Button type="submit" variant="contained" size="large" loading={laeuft}>
            {brauchtNeuesPasswort ? "Passwort setzen" : "Anmelden"}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
